import moment from "moment"
import {
  SERVICE_CATEGORIES,
  PAYMENT_METHODS,
  COACH_COMMUNICATION_METHODS,
} from "@/store/coaching/coaching.constants"
import {
  convertPointToPrice,
  convertPbCreditToUsd,
} from "@/store/utils/util.utils"
import { requestSession } from "@/api/coaching"

const state = () => ({
  selectedServiceCategory: SERVICE_CATEGORIES.SESSIONS,
  selectedService: null,
  selectedServiceAmount: 1,
  selectedRegion: null,
  selectedPlatform: null,
  inGameUsername: null,
  communicationMethod: COACH_COMMUNICATION_METHODS.DISCORD,
  discordUsername: null,
  skypeUsername: null,
  inSessionWarning: false,
  coachUnavailable: false,
  coachUnavailableMessage: null,
  coachRejected: false,
  requestCancelled: false,
  requestProgressEnded: false,
})

const mutations = {
  SET_SELECTED_SERVICE_CATEGORY: (state, serviceCategory) => {
    state.selectedServiceCategory = serviceCategory
  },
  SET_SELECTED_SERVICE: (state, service) => {
    state.selectedService = service
  },
  INCREMENT_SELECTED_SERVICE_AMOUNT: (state) => {
    state.selectedServiceAmount = Math.max(state.selectedServiceAmount + 1, 1)
  },
  DECREMENT_SELECTED_SERVICE_AMOUNT: (state) => {
    state.selectedServiceAmount = Math.max(state.selectedServiceAmount - 1, 1)
  },
  SET_SELECTED_SERVICE_AMOUNT: (state, amount) => {
    state.selectedServiceAmount = Math.max(amount, 1)
  },
  SET_SELECTED_REGION: (state, region) => {
    state.selectedRegion = region
  },
  SET_SELECTED_PLATFORM: (state, platform) => {
    state.selectedPlatform = platform
  },
  SET_IN_GAME_USERNAME: (state, inGameUsername) => {
    state.inGameUsername = inGameUsername
  },
  SET_COMMUNICATION_METHOD: (state, communicationMethod) => {
    state.communicationMethod = communicationMethod
  },
  SET_DISCORD_USERNAME: (state, discordUsername) => {
    state.discordUsername = discordUsername
  },
  SET_SKYPE_USERNAME: (state, skypeUsername) => {
    state.skypeUsername = skypeUsername
  },
  SET_IN_SESSION_WARNING: (state, isActive) => {
    state.inSessionWarning = isActive
  },
  SET_COACH_UNAVAILABLE: (state, isUnavailable) => {
    state.coachUnavailable = isUnavailable
  },
  SET_COACH_UNAVAILABLE_MESSAGE: (state, message) => {
    state.coachUnavailableMessage = message
  },
  SET_COACH_REJECTED: (state, rejected) => {
    state.coachRejected = rejected
  },
}

const actions = {
  setSelectedServiceCategory ({ commit }, serviceCategory) {
    commit("SET_SELECTED_SERVICE_CATEGORY", serviceCategory)
  },
  setSelectedService ({ commit }, service) {
    commit("SET_SELECTED_SERVICE", service)
  },
  incrementSelectedServiceAmount ({ commit }) {
    commit("INCREMENT_SELECTED_SERVICE_AMOUNT")
  },
  decrementSelectedServiceAmount ({ commit }) {
    commit("DECREMENT_SELECTED_SERVICE_AMOUNT")
  },
  setSelectedServiceAmount ({ commit }, amount) {
    commit("SET_SELECTED_SERVICE_AMOUNT", amount)
  },
  setSelectedRegion ({ commit }, region) {
    commit("SET_SELECTED_REGION", region)
  },
  setSelectedPlatform ({ commit }, platform) {
    commit("SET_SELECTED_PLATFORM", platform)
  },
  setInGameUsername ({ commit }, inGameUsername) {
    commit("SET_IN_GAME_USERNAME", inGameUsername)
  },
  setCommunicationMethod ({ commit }, communicationMethod) {
    commit("SET_COMMUNICATION_METHOD", communicationMethod)
  },
  setDiscordUsername ({ commit }, discordUsername) {
    commit("SET_DISCORD_USERNAME", discordUsername)
  },
  setSkypeUsername ({ commit }, skypeUsername) {
    commit("SET_SKYPE_USERNAME", skypeUsername)
  },
  startInSessionWarning ({ commit }) {
    commit("SET_IN_SESSION_WARNING", true)
  },
  closeInSessionWarning ({ commit }) {
    commit("SET_IN_SESSION_WARNING", false)
  },
  setCoachUnavailable ({ commit }, isUnavailable) {
    commit("SET_COACH_UNAVAILABLE", isUnavailable)
  },
  setCoachRejected ({ commit }, rejected) {
    commit("SET_COACH_REJECTED", rejected)
  },
  async requestSession ({ state, commit, dispatch, getters, rootGetters }) {
    // Reset request state
    commit("SET_COACH_UNAVAILABLE", false)
    commit("SET_COACH_UNAVAILABLE_MESSAGE", null)
    commit("SET_COACH_REJECTED", false)

    // Update current request metadata
    const selectedCoach = rootGetters["Coaching/CoachWidget/selectedCoach"] || null
    const selectedGame = rootGetters["Coaching/CoachWidget/selectedGame"] || null
    await Promise.all([
      dispatch(
        "Coaching/CoachWidget/setCurrentRequestCoach",
        selectedCoach,
        { root: true }
      ),
      dispatch(
        "Coaching/CoachWidget/setCurrentRequestType",
        state.selectedService,
        { root: true }
      ),
      dispatch(
        "Coaching/CoachWidget/setCurrentRequestGame",
        selectedGame,
        { root: true }
      ),
      dispatch(
        "Coaching/CoachWidget/setCurrentRequestCommunicationMethod",
        state.communicationMethod,
        { root: true }
      ),
      dispatch(
        "Coaching/CoachWidget/setCurrentRequestCreated",
        moment.utc(),
        { root: true }
      ),
      dispatch(
        "Coaching/CoachWidget/setCurrentRequestGames",
        state.selectedServiceAmount,
        { root: true }
      ),
    ])

    // Construct API request body
    const instaProAccessToken = await dispatch(
      "User/Coaching/loadInstaProAccessToken",
      null,
      { root: true }
    )

    let communicationUsername = null
    switch (state.communicationMethod) {
      case COACH_COMMUNICATION_METHODS.DISCORD:
        communicationUsername = state.discordUsername
        break
      case COACH_COMMUNICATION_METHODS.SKYPE:
        communicationUsername = state.skypeUsername
        break
    }

    const body = {
      coach_id: selectedCoach?.id,
      game: selectedGame,
      game_id: selectedCoach?.games[selectedGame].id,
      type: state.selectedService?.session_type?.key,
      platform: state.selectedPlatform,
      message: "fake message",
      region: state.selectedRegion,
      total: state.selectedServiceAmount,
      in_game_username: state.inGameUsername,
      communication_type: state.communicationMethod,
      communication_name: communicationUsername,
      payment_method: getters.selectedPaymentMethod,
    }

    try {
      throw new Error("Sessions can no longer be requested.")
      const data = await requestSession(body, instaProAccessToken)
      dispatch(
        "Coaching/CoachWidget/setCurrentRequestId",
        data.request_id,
        { root: true }
      )
    } catch (e) {
      const errorCode = e.response?.status
      if (errorCode === 403) {
        commit("SET_COACH_UNAVAILABLE_MESSAGE", "Insufficient funds")
      }
      commit("SET_COACH_UNAVAILABLE", true)
    }
  },
}

const getters = {
  selectedServiceCategory: state => state.selectedServiceCategory,
  selectedService: state => state.selectedService,
  selectedServiceAmount: state => state.selectedServiceAmount,
  selectedRegion: state => state.selectedRegion,
  selectedPlatform: state => state.selectedPlatform,
  inGameUsername: state => state.inGameUsername,
  communicationMethod: state => state.communicationMethod,
  discordUsername: state => state.discordUsername,
  skypeUsername: state => state.skypeUsername,
  // Synced state from general coaching service order flow
  selectedPaymentMethod: (state, getters, rootState, rootGetters) => {
    return rootGetters["Coaching/CoachService/selectedPaymentMethod"]
  },
  totalServiceCostInUsd: (state, getters, rootState, rootGetters) => {
    const getUserPriceForCoachService = rootGetters["Coaching/CoachService/getUserPriceForCoachService"]
    const serviceCostInUsd = getUserPriceForCoachService(state.selectedService)
    if (serviceCostInUsd === null) {
      return null
    }

    return serviceCostInUsd * state.selectedServiceAmount
  },
  currentBalanceInUsd: (state, getters, rootState, rootGetters) => {
    switch (getters.selectedPaymentMethod) {
      case PAYMENT_METHODS.PG_POINTS:
        const userPgPoints = rootGetters["User/Inventory/getPGPoints"] || 0
        return convertPointToPrice(userPgPoints)
      case PAYMENT_METHODS.PB_CREDIT:
        const userPbCredit = rootGetters["User/Inventory/getPbCredit"] || 0
        return convertPbCreditToUsd(userPbCredit)
      default:
        return 0
    }
  },
  remainingBalanceInUsd: (state, getters) => {
    return getters.currentBalanceInUsd - getters.totalServiceCostInUsd
  },
  userHasSufficientFunds: (state, getters) => {
    return getters.remainingBalanceInUsd >= 0
  },
  inSessionWarning: state => state.inSessionWarning,
  coachUnavailable: state => state.coachUnavailable,
  coachUnavailableMessage: state => state.coachUnavailableMessage,
  coachRejected: state => state.coachRejected,
  requestCancelled: state => state.requestCancelled,
  requestProgressEnded: state => state.requestProgressEnded,
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}



// WEBPACK FOOTER //
// ./src/store/coaching/widget/service-checkout/service-checkout.module.js